import React from 'react';
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';
import { AppBar, Toolbar } from '@material-ui/core';
import colors from '../../../theme/colors';

export type BasePageProps = {
  /** The text to display as the title */
  title: string | React.ReactNode;
  /** The page's content */
  children?: React.ReactNode;
  /** Content to display on left side */
  leftActions?: React.ReactNode;
  /** Content to display on the right side */
  rightActions?: React.ReactNode;
  /** Which color to use as the toolbar's background */
  toolbarColor?: string;
  /** Which color to use as the toolbar's text */
  textColor?: string;
  /** Padding multiplier to use on the content */
  padding?: number;
  /** Defines how the header should behave */
  appBarPosition?: 'static' | 'fixed' | 'sticky';
  /** Aligns the title */
  titleAlignment?: 'center' | 'left' | 'right';
  addContentPadding?: boolean;
};

type StyleProps = {
  padding: number;
};

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      flexGrow: 1,
    },
    content: {
      padding: (props: StyleProps): number => theme.spacing(props.padding),
    },
    toolbarTitle: {
      flex: 3,
      margin: theme.spacing(1),
      /* Default typography HeaderText */
      fontWeight: 600,
      fontSize: 16,
      lineHeight: '24px',
    },
    leftItems: {
      alignSelf: 'left',
      display: 'flex',
      flexDirection: 'row',
    },
    rightItems: {
      alignSelf: 'right',
      display: 'flex',
      flexDirection: 'row-reverse',
    },
    fixedAppbarOffset: theme.mixins.toolbar,
  }),
);

const BasePage = ({
  title,
  children,
  leftActions = null,
  rightActions = null,
  textColor = colors.white,
  toolbarColor = colors.madison,
  padding = 1,
  appBarPosition = 'static',
  titleAlignment = 'center',
}: BasePageProps): JSX.Element => {
  const classes = useStyles({ padding });
  return (
    <div className={classes.root}>
      <AppBar
        data-testid="appbar"
        position={appBarPosition}
        elevation={0}
        style={{ backgroundColor: toolbarColor }}
      >
        <Toolbar>
          <div className={classes.leftItems}>
            {leftActions !== null && leftActions}
          </div>
          <div
            style={{ textAlign: titleAlignment, color: textColor }}
            className={classes.toolbarTitle}
            data-testid="title"
          >
            {title}
          </div>
          <div className={classes.rightItems}>
            {rightActions !== null && rightActions}
          </div>
        </Toolbar>
      </AppBar>
      {appBarPosition === 'fixed' && (
        <div
          data-testid="fixed-appbar-offset"
          className={classes.fixedAppbarOffset}
        />
      )}
      <div className={classes.content}>{children}</div>
    </div>
  );
};

export default BasePage;
